import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, ApiProblem } from '@/lib/api';
import { MODEL } from '@/app/queryClient';
import { formatMoney, shortDate } from '@/lib/format';
import { BasisBadge, Card, EmptyState, ErrorState, Skeleton } from '@/components';

interface ForecastDay {
  date: string;
  inflow: number;
  outflow: number;
  closing_cash: number;
}

interface ForecastResponse {
  days: ForecastDay[];
  assumptions: { label: string; value: string; source?: string }[];
  buffer_floor: number | null;
}

function pointAt(days: readonly ForecastDay[], offset: number): ForecastDay | undefined {
  return days[Math.min(days.length - 1, offset - 1)];
}

function biggestOutflow(days: readonly ForecastDay[], from: number, to: number): ForecastDay | null {
  const slice = days.slice(from, to);
  if (slice.length === 0) return null;
  return slice.reduce((worst, day) => (day.outflow > worst.outflow ? day : worst), slice[0] as ForecastDay);
}

/**
 * Projected cash at 30, 60 and 90 days with the biggest scheduled outflow named under each.
 * It is a model and says so: the badge reads MODEL and the assumptions are listed on the
 * Finance page rather than hidden in a tooltip nobody opens.
 */
export function CashForecast() {
  const query = useQuery({
    queryKey: ['metrics', 'home-forecast', '90d'],
    queryFn: () =>
      api.get<ForecastResponse>('/finance/forecast', { query: { horizon: 90, scenario: 'base' } }),
    ...MODEL,
  });

  if (query.isLoading) {
    return (
      <Card title="Cash forecast">
        <Skeleton shape="card" lines={2} label="Loading the forecast" />
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card title="Cash forecast">
        <ErrorState
          message={query.error instanceof ApiProblem ? query.error.message : 'The forecast did not load.'}
          onRetry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const days = query.data?.days ?? [];
  if (days.length === 0) {
    return (
      <Card title="Cash forecast">
        <EmptyState message="No forecast yet. It needs a full 30 days of paid history." />
      </Card>
    );
  }

  const horizons = [30, 60, 90] as const;

  return (
    <Card
      title="Cash forecast"
      action={
        <Link to="/finance/forecast" className="o-btn o-btn--ghost o-btn--sm">
          Open the forecast
        </Link>
      }
      id="cash-forecast"
    >
      <div className="o-row">
        <BasisBadge basis="model" />
        <span className="o-muted">Cash basis. Never shown in the same table as the accrual profit and loss.</span>
      </div>

      <div className="o-grid o-grid--tiles">
        {horizons.map((horizon, index) => {
          const point = pointAt(days, horizon);
          const outflow = biggestOutflow(days, index * 30, horizon);
          return (
            <div className="o-kpi" key={horizon}>
              <span className="o-kpi__label">Day {horizon}</span>
              <span className="o-kpi__value">{formatMoney(point?.closing_cash ?? null)}</span>
              {outflow ? (
                <span className="o-kpi__meta">
                  biggest outflow {formatMoney(outflow.outflow)} on {shortDate(outflow.date)}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {query.data?.buffer_floor !== null && query.data?.buffer_floor !== undefined ? (
        <p className="o-muted">Buffer floor {formatMoney(query.data.buffer_floor)}.</p>
      ) : null}
    </Card>
  );
}
